import { request } from './client'

/* **W-128 — EVERY WORD THIS PRODUCT INVENTED.** Sprint 59.

   Every Arabic name in this build is ours: a developer wrote "غرفة لأربعة" for a room type
   and "منشفة حمام" for a towel, and each was a guess at what this hotel calls the thing. The
   client settled what happens about that on 18 September (CQ-130) — the invented words stay
   and the hotel corrects them during go-live — and nine kinds of thing had an Arabic column,
   an invented value in it, and no screen that could write it.

   ONE EDITABLE COLUMN: the Arabic. The English is shown and not offered, which is the
   difference between this screen and W-129's settings — there a hotel renames its own roles
   and its own kinds of guest request, and the English is theirs; here the English is the
   product's label and it is what makes the Arabic box answerable at all. */

/** The kinds of thing this screen can translate. The wire words, exactly. */
export type TranslatedKind =
  | 'BUILDING' | 'ZONE' | 'ROOM_TYPE' | 'AREA'
  | 'STOCK_ITEM' | 'STOCK_LOCATION' | 'TASK_TYPE' | 'SHIFT' | 'TEMPLATE'

export interface TranslationRow {
  kind: TranslatedKind
  id: string
  /** `TWN`, `TOWEL_BATH`. Never changes, never translated — what a report counts by. */
  code: string
  /** The product's own label. Shown so the Arabic box is answerable; never editable here. */
  name: string
  nameAr: string | null
  /** Forty rooms of this type, nine areas in this zone. Null where it was not counted. */
  inUse: number | null
}

export interface TranslationGroup {
  kind: TranslatedKind
  total: number
  translated: number
  rows: TranslationRow[]
}

export interface TranslationView {
  /** In the order a hotel is built: buildings and zones before the rooms inside them. */
  groups: TranslationGroup[]
  total: number
  translated: number
  /** **NULL WHEN THERE IS NOTHING TO TRANSLATE** — not 0 and not 1. A hotel that has
   *  loaded nothing has translated nothing and is missing nothing, and a full bar over an
   *  empty list is a lie. The screen says it in words. */
  fraction: number | null
}

export const fetchTranslations = (
  propertyId: string,
  options: { signal?: AbortSignal } = {},
) => request<TranslationView>(
  `/properties/${propertyId}/translations`,
  options.signal ? { signal: options.signal } : {},
)

/**
 * One word. **Empty clears it**, which is a real answer — a hotel deciding the English word
 * is the word in both languages says so by emptying the box, and the row goes back onto the
 * "still to do" count where the decision stays visible.
 *
 * Returns the WHOLE view, because correcting one word moves the count at the top of the
 * screen and the count on its group.
 */
export const saveTranslation = (
  propertyId: string,
  kind: TranslatedKind,
  id: string,
  nameAr: string,
) => request<TranslationView>(
  `/properties/${propertyId}/translations/${kind}/${id}`,
  { method: 'PUT', body: { nameAr } },
)

/** How many words are still ours, for the line at the top of the screen. */
export const stillOurs = (view: TranslationView): number => view.total - view.translated
